// src/services/sessionEvents.ts
// Why a tab was sent to the login page, and how deliberate logouts reach
// the other tabs.

import { getAuthToken } from '../utils/cookieUtils';

const WRITE_API_URL = process.env.REACT_APP_FLUX_API_URL;

/**
 * Every reason a tab can be sent to the login page. Flux only records codes
 * from this list (Web.SessionEventController), so keep the two in step.
 */
export type LogoutReason =
  | 'user_logout'
  | 'logged_out_in_another_tab'
  | 'different_user_signed_in'
  | 'server_force_logout'
  | 'socket_unauthorized'
  | 'refresh_rejected'
  | 'retry_unauthorized'
  | 'validation_unauthorized'
  | 'no_stored_session';

/**
 * Tell flux why this tab is leaving for the login page. Sends only the reason
 * code, the page path and the HTTP status that triggered it. The user is
 * identified server side from the access token, which is read here before the
 * caller clears it. Fire-and-forget: `keepalive` lets it finish even when the
 * caller navigates away immediately, and a failure never blocks the logout.
 */
export function reportSessionEnd(reason: LogoutReason, httpStatus?: number): void {
  try {
    const token = getAuthToken();
    fetch(`${WRITE_API_URL}/admin/session_event`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({
        reason,
        path: window.location.pathname,
        ...(httpStatus ? { http_status: httpStatus } : {})
      })
    }).catch(() => {});
  } catch {
    // Reporting is best effort.
  }
}

// ============================================
// Cross-tab logout
// ============================================

const AUTH_CHANNEL_NAME = 'freightroll_auth';

type AuthMessage = { type: 'logout'; reason: LogoutReason };

let authChannel: BroadcastChannel | null = null;
try {
  authChannel = new BroadcastChannel(AUTH_CHANNEL_NAME);
} catch {
  // Not supported; useForceLogout falls back to watching localStorage.
}

export function isAuthChannelSupported(): boolean {
  return authChannel !== null;
}

/**
 * Tell the other tabs this browser's session was ended on purpose (the user
 * logged out, or the server ended it). Only deliberate logouts go out here; a
 * tab that hit a problem on its own signs out alone.
 */
export function broadcastLogout(reason: LogoutReason): void {
  authChannel?.postMessage({ type: 'logout', reason } as AuthMessage);
}

/** Subscribe to logouts announced by other tabs. Returns an unsubscribe function. */
export function onLogoutBroadcast(handler: (reason: LogoutReason) => void): () => void {
  if (!authChannel) return () => {};

  const listener = (event: MessageEvent<AuthMessage>) => {
    if (event.data?.type === 'logout') handler(event.data.reason);
  };
  authChannel.addEventListener('message', listener as EventListener);
  return () => authChannel?.removeEventListener('message', listener as EventListener);
}

// ============================================
// Connection problems
// ============================================

const CONNECTION_PROBLEM_EVENT = 'freightroll:connection-problem';

/**
 * Signal that the server couldn't be reached or answered with an error, as
 * opposed to refusing the session. The app shows a notice and keeps the user
 * where they are.
 */
export function notifyConnectionProblem(): void {
  window.dispatchEvent(new Event(CONNECTION_PROBLEM_EVENT));
}

export function onConnectionProblem(handler: () => void): () => void {
  window.addEventListener(CONNECTION_PROBLEM_EVENT, handler);
  return () => window.removeEventListener(CONNECTION_PROBLEM_EVENT, handler);
}
